<template>
  <div class="na-page home">
    <AppNavigation />

    <!-- ═════════════ Hero ═════════════ -->
    <section class="hero na-ruled na-below-nav">
      <div class="hero-margin" aria-hidden="true"></div>
      <div class="na-wrap">
        <div class="dateline">
          <span>Vol. I — No. 1</span>
          <span class="dl-star" aria-hidden="true">✦</span>
          <span>{{ today }}</span>
          <span class="dl-star" aria-hidden="true">✦</span>
          <span>Digital Edition</span>
        </div>

        <div class="hero-grid">
          <div class="hero-copy">
            <span class="na-flag">For local newspapers &amp; town halls</span>
            <h1 class="na-display hero-title">Your town's newspaper, <em>now in every pocket.</em></h1>
            <p class="na-lede hero-lede">
              NewsAppify turns your paper into branded iPhone and Android apps, a fast website and
              nine automatic email newsletters — all run from one newsroom. Your staff writes;
              we build it, host it and keep it running.
            </p>
            <div class="hero-actions">
              <router-link to="/demo" class="na-btn na-btn-primary na-btn-lg">Request a Demo<v-icon size="20">mdi-arrow-right</v-icon></router-link>
              <a href="#pricing" class="na-btn na-btn-outline na-btn-lg" @click.prevent="scrollTo('pricing')">See pricing</a>
            </div>
            <dl class="hero-stats">
              <div><dt>To go live</dt><dd>2–4 wks</dd></div>
              <div><dt>Newsletters, automatic</dt><dd>9</dd></div>
              <div><dt>Per push alert</dt><dd>$0</dd></div>
            </dl>
          </div>

          <!-- Composition: the print edition becomes the app -->
          <div class="hero-art" aria-label="The Springfield Gazette print front page next to the Springfield Gazette phone app">
            <!-- Print front page -->
            <div class="paper">
              <div class="paper-mast">The Springfield Gazette</div>
              <div class="paper-dateline"><span>Thursday</span><span>Springfield's paper since 1887</span><span>$1.50</span></div>
              <div class="paper-headline">Council approves Main Street plan</div>
              <div class="paper-cols">
                <div class="paper-photo"></div>
                <div class="paper-lines"><i></i><i></i><i></i><i></i><i></i><i class="short"></i></div>
                <div class="paper-lines"><i></i><i></i><i></i><i></i><i></i><i></i><i class="short"></i></div>
              </div>
              <div class="paper-stamp">Now in the app</div>
            </div>

            <!-- Phone -->
            <div class="phone">
              <div class="phone-screen">
                <div class="ph-status"><span>9:41</span><span class="ph-island"></span><span class="ph-icons"><v-icon size="11" color="#fff">mdi-signal</v-icon><v-icon size="11" color="#fff">mdi-wifi</v-icon><v-icon size="12" color="#fff">mdi-battery</v-icon></span></div>
                <div class="ph-bar">
                  <div>
                    <div class="ph-greet">Good morning</div>
                    <div class="ph-mast">Springfield Gazette</div>
                  </div>
                  <div class="ph-bell"><v-icon size="17" color="#fff">mdi-bell-outline</v-icon><span></span></div>
                </div>
                <div class="ph-ticker"><b>Breaking</b><span>Oak Ave closed for water main repair until 4 PM</span></div>
                <div class="ph-body">
                  <div class="ph-section"><span></span>Top stories<span></span></div>
                  <div class="ph-hero">
                    <div class="ph-hero-img">
                      <div class="ph-hero-cap">
                        <small>Local news</small>
                        Mayor unveils $2M park revitalization project
                      </div>
                    </div>
                    <div class="ph-meta"><span>By James Carter</span><span>2 hours ago</span></div>
                  </div>
                  <div v-for="s in phoneStories" :key="s.title" class="ph-row">
                    <div>
                      <small :style="{ color: s.color }">{{ s.cat }}</small>
                      <div class="ph-row-title">{{ s.title }}</div>
                      <div class="ph-row-ago">{{ s.ago }}</div>
                    </div>
                    <div class="ph-thumb" :style="{ background: s.color }"><v-icon size="18" color="#fff">{{ s.icon }}</v-icon></div>
                  </div>
                </div>
                <div class="ph-nav">
                  <span class="on"><v-icon size="19">mdi-home</v-icon>Home</span>
                  <span><v-icon size="19">mdi-newspaper-variant-outline</v-icon>Sections</span>
                  <span><v-icon size="19">mdi-calendar-outline</v-icon>Events</span>
                  <span><v-icon size="19">mdi-account-circle-outline</v-icon>Account</span>
                </div>
              </div>
            </div>

            <!-- Email card -->
            <div class="mail-card">
              <div class="mail-icon"><v-icon size="16" color="#fff">mdi-email</v-icon></div>
              <div>
                <div class="mail-title">Morning Edition</div>
                <div class="mail-sub">Sent to 1,284 subscribers at 6:00 AM</div>
              </div>
            </div>

            <!-- Push toast -->
            <div class="toast">
              <div class="toast-check"><v-icon size="18" color="#fff">mdi-check</v-icon></div>
              <div>
                <div class="toast-title">Breaking alert delivered</div>
                <div class="toast-sub">Every phone with the app, in under a minute</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═════════════ How it works (a true sequence) ═════════════ -->
    <section class="na-section">
      <div class="na-wrap">
        <div class="section-head">
          <span class="na-flag">The process</span>
          <h2 class="na-h2">From newspaper to mobile app in three steps</h2>
          <p class="na-body">No coding and no IT department. You keep publishing the way you do now.</p>
        </div>
        <ol class="steps">
          <li v-for="(s, i) in steps" :key="s.title" class="step na-card-surface">
            <div class="step-top">
              <span class="step-num">{{ i + 1 }}</span>
              <v-icon size="28" color="#B8925A">{{ s.icon }}</v-icon>
            </div>
            <h3 class="na-h3">{{ s.title }}</h3>
            <p class="step-text">{{ s.text }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- ═════════════ A day on the Gazette (a real schedule) ═════════════ -->
    <section class="na-section na-band-2">
      <div class="na-wrap day-grid">
        <div class="day-intro">
          <span class="na-flag">Runs on its own</span>
          <h2 class="na-h2">A day on the Springfield Gazette</h2>
          <p class="na-body">
            Your staff writes the stories. The platform handles the rest on schedule — so a
            three-person newsroom can keep a town as informed as a big-city daily.
          </p>
        </div>
        <ol class="day-list">
          <li v-for="d in day" :key="d.time" class="day-item" :class="{ 'day-breaking': d.breaking }">
            <time class="day-time">{{ d.time }}</time>
            <div>
              <h3 class="day-title">{{ d.title }}</h3>
              <p class="day-text">{{ d.text }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- ═════════════ Publish once ═════════════ -->
    <section class="na-section">
      <div class="na-wrap channels-grid">
        <div>
          <span class="na-flag">Publish once</span>
          <h2 class="na-h2">One newsroom. Every place residents look.</h2>
          <p class="na-body">
            A story your editor publishes appears in the apps, on the website and in the next
            newsletter at the same time. Residents sign in once, and their saved stories, RSVPs and
            newsletter choices follow them from phone to laptop.
          </p>
          <router-link to="/features" class="na-btn na-btn-outline channels-link">Explore every feature<v-icon size="19">mdi-arrow-right</v-icon></router-link>
        </div>
        <ul class="channels na-card-surface">
          <li v-for="c in channels" :key="c.title" class="channel">
            <div class="channel-icon"><v-icon size="24" color="#14315E">{{ c.icon }}</v-icon></div>
            <div>
              <h3 class="channel-title">{{ c.title }}</h3>
              <p class="channel-text">{{ c.text }}</p>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <!-- ═════════════ Community ═════════════ -->
    <section class="na-section na-band-2">
      <div class="na-wrap">
        <div class="section-head">
          <span class="na-flag">Residents take part</span>
          <h2 class="na-h2">A paper people write back to</h2>
          <p class="na-body">
            Every submission lands in your newsroom for review. Tips are sorted by topic and checked
            for spam automatically, and anything urgent is emailed to your editors right away.
          </p>
        </div>
        <div class="community">
          <div v-for="c in community" :key="c.title" class="community-item">
            <div class="community-icon"><v-icon size="22" color="#14315E">{{ c.icon }}</v-icon></div>
            <h3 class="community-title">{{ c.title }}</h3>
            <p class="community-text">{{ c.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ═════════════ Pricing ═════════════ -->
    <section id="pricing" class="na-section">
      <div class="na-wrap">
        <div class="section-head pricing-head">
          <span class="na-flag">Pricing</span>
          <h2 class="na-h2">One complete platform, priced by the size of your town</h2>
          <p class="na-body">
            A custom-built app typically costs $15,000 to $50,000, and printing one edition can run
            $2,000. NewsAppify includes the apps, website, newsletters, newsroom and our team.
          </p>
        </div>
        <PricingPlans faq />
      </div>
    </section>

    <!-- ═════════════ Final call to action ═════════════ -->
    <section class="na-section na-band-ink final">
      <div class="na-wrap final-inner">
        <div class="na-ornament final-orn"><span aria-hidden="true">✦</span></div>
        <p class="final-kicker">Extra! Extra!</p>
        <h2 class="na-h2 final-title">Your town's digital edition starts today.</h2>
        <p class="na-lede final-lede">Tell us about your paper and we'll build a sample app with your town's name on it before we talk. It's free.</p>
        <div class="final-actions">
          <router-link to="/demo" class="na-btn na-btn-brass na-btn-lg">Request a Demo<v-icon size="20">mdi-arrow-right</v-icon></router-link>
          <router-link to="/why" class="na-btn na-btn-ghost na-btn-lg">Why NewsAppify</router-link>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import AppNavigation from '@/components/AppNavigation.vue'
import AppFooter from '@/components/AppFooter.vue'
import PricingPlans from '@/components/PricingPlans.vue'
import '@/assets/na-theme.css'

export default {
  name: 'NewsAppifyHome',
  components: { AppNavigation, AppFooter, PricingPlans },
  data: () => ({
    phoneStories: [
      { cat: 'Events', title: 'Fall Harvest Festival — 214 going', ago: '4h ago', icon: 'mdi-party-popper', color: '#B8925A' },
      { cat: 'Schools', title: 'Lincoln Elementary wins state science award', ago: '6h ago', icon: 'mdi-school', color: '#14315E' },
    ],
    steps: [
      { icon: 'mdi-brush-variant', title: 'We build your app', text: 'Your name, logo and colors on the apps, the website and every email. We handle the design, the app store listings and the hosting.' },
      { icon: 'mdi-pencil-outline', title: 'Your staff publishes', text: 'Editors write in the newsroom dashboard. Stories, events and obituaries go live everywhere the moment they\'re published.' },
      { icon: 'mdi-account-group-outline', title: 'Residents follow along', text: 'People download the app, sign up for newsletters and RSVP to events — and you see what they read and open.' },
    ],
    day: [
      { time: '5:00 AM', title: 'The forecast updates itself', text: 'The homepage weather refreshes from a free forecast service, and again at noon.' },
      { time: '6:00 AM', title: 'Morning Edition goes out', text: 'Subscribers get the day\'s top stories and events by email — skipped automatically on days with nothing new.' },
      { time: '9:00 AM', title: 'Event reminders', text: 'Everyone who RSVP\'d to tomorrow\'s events gets a reminder with the time, the place and a calendar link.' },
      { time: '11:40 AM', title: 'An urgent tip comes in', text: 'A resident reports a gas leak. It\'s sorted by topic, flagged urgent and emailed to your editors right away.' },
      { time: '3:12 PM', title: 'A breaking story is published', breaking: true, text: 'Every phone with the app gets an alert, Breaking News subscribers get an email, and either one opens the story.' },
      { time: 'Sunday', title: 'Personal weekly digests', text: 'Each signed-in reader gets stories from the sections they read most, plus the events they\'re going to.' },
      { time: 'Monday', title: 'The newsroom report', text: 'Admins get the most-read stories, newsletter numbers, email usage and the busiest upcoming events.' },
    ],
    channels: [
      { icon: 'mdi-cellphone', title: 'iPhone and Android apps', text: 'Native apps listed under your paper\'s name, with breaking-news alerts, saved stories, search and reading offline.' },
      { icon: 'mdi-monitor', title: 'Your website', text: 'News, sports, schools, public safety, events, opinion and obituaries — with links that open straight to the story.' },
      { icon: 'mdi-email-newsletter', title: 'Email newsletters', text: 'Nine lists from Morning Edition to Sports Friday, sent on schedule, with one-click unsubscribe and open rates.' },
      { icon: 'mdi-view-dashboard-outline', title: 'The newsroom', text: 'Admin, staff and contributor dashboards to write stories, approve submissions and edit every box on every page.' },
    ],
    community: [
      { icon: 'mdi-email-edit-outline', title: 'Letters and opinion', text: 'Residents send letters to the editor and op-eds for your editors to review.' },
      { icon: 'mdi-incognito', title: 'Anonymous tips', text: 'News tips with or without a name, triaged automatically.' },
      { icon: 'mdi-calendar-plus', title: 'Community events', text: 'Groups submit their own events; residents RSVP and add them to their calendars.' },
      { icon: 'mdi-scoreboard-outline', title: 'Game scores', text: 'Coaches and parents send in scores for the sports page.' },
      { icon: 'mdi-book-open-page-variant-outline', title: 'Obituary guestbooks', text: 'Friends and neighbors leave condolences, shown once an editor approves them.' },
      { icon: 'mdi-trophy-outline', title: 'Quiz, polls and badges', text: 'A weekly news quiz with a leaderboard, polls with published results and badges for regular readers.' },
    ],
  }),
  computed: {
    today() { return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) },
  },
  mounted() {
    if (this.$route && this.$route.hash === '#pricing') this.$nextTick(() => this.scrollTo('pricing'))
  },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' })
    },
  },
}
</script>

<style scoped>
/* ═════════════ Hero ═════════════ */
.hero { position: relative; padding-bottom: 110px; overflow: hidden; }
.hero-margin { position: absolute; top: 0; bottom: 0; left: max(16px, calc(50% - 640px)); width: 1px; background: rgba(184, 146, 90, 0.45); }
.hero-margin::after { content: ''; position: absolute; top: 0; bottom: 0; left: 4px; width: 1px; background: rgba(184, 146, 90, 0.25); }

.dateline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 18px;
  padding: 14px 0;
  margin-top: 34px;
  border-top: 1px solid var(--na-rule);
  border-bottom: 3px double var(--na-rule);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--na-ink-2);
}
.dl-star { color: var(--na-brass); }

.hero-grid { display: grid; grid-template-columns: minmax(0, 1.02fr) minmax(0, 0.98fr); gap: 40px; align-items: center; padding-top: 56px; }
.hero-title { max-width: 10.5em; }
.hero-lede { margin-top: 26px; }
.hero-actions { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 34px; }

.hero-stats { display: grid; grid-template-columns: repeat(3, auto); justify-content: start; gap: 0; margin: 48px 0 0; border-top: 1px solid var(--na-rule); padding-top: 22px; }
.hero-stats div { padding: 0 34px 0 0; margin-right: 34px; border-right: 1px solid var(--na-rule); }
.hero-stats div:last-child { border-right: 0; margin-right: 0; }
.hero-stats dt { font-size: 0.9rem; color: var(--na-ink-2); }
.hero-stats dd { margin: 4px 0 0; font-family: var(--na-serif); font-weight: 700; font-size: 2.2rem; line-height: 1; color: var(--na-navy); }

/* Composition */
.hero-art { position: relative; height: 640px; }

.paper {
  position: absolute;
  left: 0;
  top: 70px;
  width: 300px;
  padding: 18px 18px 22px;
  background: #FBF8F0;
  border: 1px solid #E3D9C6;
  box-shadow: 0 30px 60px -30px rgba(16, 27, 45, 0.45), 0 2px 0 #fff inset;
  transform: rotate(-7deg);
  color: #1B1B1B;
}
.paper-mast { font-family: var(--na-serif); font-weight: 800; font-size: 1.32rem; text-align: center; letter-spacing: -0.01em; border-bottom: 2px solid #1B1B1B; padding-bottom: 6px; }
.paper-dateline { display: flex; justify-content: space-between; font-size: 0.55rem; text-transform: uppercase; letter-spacing: 0.08em; padding: 4px 0; border-bottom: 1px solid #1B1B1B; color: #555; }
.paper-headline { font-family: var(--na-serif); font-weight: 800; font-size: 1.28rem; line-height: 1.1; margin: 12px 0 10px; }
.paper-cols { display: grid; grid-template-columns: 1.2fr 1fr 1fr; gap: 10px; }
.paper-photo { height: 96px; background: linear-gradient(160deg, #8E9AA6, #5D6976); filter: grayscale(1); }
.paper-lines { display: flex; flex-direction: column; gap: 6px; padding-top: 2px; }
.paper-lines i { display: block; height: 4px; background: #C9C2B4; border-radius: 1px; }
.paper-lines i.short { width: 60%; }
.paper-stamp {
  position: absolute;
  right: 6px;
  bottom: 36px;
  padding: 6px 12px;
  border: 3px solid var(--na-navy);
  border-radius: 6px;
  color: var(--na-navy);
  font-family: var(--na-sans);
  font-weight: 800;
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transform: rotate(-12deg);
  opacity: 0.85;
  background: rgba(251, 248, 240, 0.6);
}

.phone {
  position: absolute;
  right: 18px;
  top: 0;
  width: 300px;
  height: 620px;
  padding: 10px;
  border-radius: 46px;
  background: #0E1726;
  box-shadow: 0 0 0 1.5px #2A3445, 0 50px 90px -40px rgba(11, 30, 59, 0.75), 0 20px 40px -20px rgba(11, 30, 59, 0.35);
  transform: rotate(3deg);
}
.phone-screen { height: 100%; border-radius: 37px; overflow: hidden; background: #F7F3EA; display: flex; flex-direction: column; position: relative; }
.ph-status { display: flex; justify-content: space-between; align-items: center; padding: 12px 20px 6px; background: var(--na-navy); color: #fff; font-size: 0.72rem; font-weight: 700; }
.ph-island { width: 86px; height: 24px; border-radius: 14px; background: #000; }
.ph-icons { display: flex; gap: 3px; }
.ph-bar { display: flex; justify-content: space-between; align-items: center; padding: 8px 18px 14px; background: var(--na-navy); color: #fff; }
.ph-greet { font-size: 0.68rem; opacity: 0.7; }
.ph-mast { font-family: var(--na-serif); font-weight: 700; font-size: 1.12rem; }
.ph-bell { position: relative; width: 32px; height: 32px; border-radius: 50%; background: rgba(255, 255, 255, 0.14); display: flex; align-items: center; justify-content: center; }
.ph-bell span { position: absolute; top: 6px; right: 7px; width: 7px; height: 7px; border-radius: 50%; background: var(--na-brass); }
.ph-ticker { display: flex; gap: 8px; align-items: center; padding: 7px 12px; background: #F1E5CC; font-size: 0.68rem; white-space: nowrap; overflow: hidden; }
.ph-ticker b { background: var(--na-press); color: #fff; padding: 2px 6px; border-radius: 3px; letter-spacing: 0.06em; text-transform: uppercase; font-size: 0.58rem; }
.ph-ticker span { overflow: hidden; text-overflow: ellipsis; font-family: var(--na-serif); color: #222; }
.ph-body { flex: 1; padding: 10px 12px 0; overflow: hidden; }
.ph-section { display: flex; align-items: center; gap: 8px; font-size: 0.58rem; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: var(--na-navy); margin-bottom: 8px; }
.ph-section span { flex: 1; height: 1px; background: var(--na-navy); opacity: 0.4; }
.ph-hero { background: #fff; border-radius: 10px; overflow: hidden; box-shadow: 0 6px 16px -10px rgba(0, 0, 0, 0.3); }
.ph-hero-img { height: 128px; background: linear-gradient(180deg, rgba(0, 0, 0, 0) 30%, rgba(6, 18, 36, 0.85) 100%), linear-gradient(135deg, #5E8C6A 0%, #2F5E46 45%, #1F3D57 100%); display: flex; align-items: flex-end; }
.ph-hero-cap { padding: 10px 12px; color: #fff; font-family: var(--na-serif); font-weight: 700; font-size: 0.86rem; line-height: 1.22; }
.ph-hero-cap small { display: block; font-family: var(--na-sans); font-size: 0.55rem; letter-spacing: 0.16em; text-transform: uppercase; color: #E6CFA6; margin-bottom: 3px; }
.ph-meta { display: flex; justify-content: space-between; padding: 6px 10px; font-size: 0.6rem; color: #777; }
.ph-row { display: flex; justify-content: space-between; gap: 10px; padding: 10px 2px; border-bottom: 1px solid #E8E0D0; }
.ph-row small { font-size: 0.55rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; }
.ph-row-title { font-family: var(--na-serif); font-weight: 700; font-size: 0.78rem; line-height: 1.25; color: #1B1B1B; }
.ph-row-ago { font-size: 0.58rem; color: #999; margin-top: 2px; }
.ph-thumb { width: 46px; height: 46px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ph-nav { display: flex; justify-content: space-around; padding: 9px 6px 16px; background: #fff; border-top: 1px solid #E8E0D0; }
.ph-nav span { display: flex; flex-direction: column; align-items: center; gap: 2px; font-size: 0.56rem; color: #8A8F98; }
.ph-nav span .v-icon { color: #8A8F98 !important; }
.ph-nav span.on, .ph-nav span.on .v-icon { color: var(--na-navy) !important; font-weight: 700; }

.mail-card {
  position: absolute;
  right: -6px;
  top: 128px;
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 12px 16px 12px 12px;
  background: var(--na-card);
  border: 1px solid var(--na-rule);
  border-radius: 10px;
  box-shadow: 0 20px 40px -22px rgba(16, 27, 45, 0.45);
  transform: translateX(18%);
}
.mail-icon { width: 32px; height: 32px; border-radius: 8px; background: var(--na-brass); display: flex; align-items: center; justify-content: center; }
.mail-title { font-weight: 700; font-size: 0.9rem; }
.mail-sub { font-size: 0.78rem; color: var(--na-ink-2); }

.toast {
  position: absolute;
  left: 40px;
  bottom: 30px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 22px 16px 16px;
  background: var(--na-navy-deep);
  color: #fff;
  border-radius: 10px;
  box-shadow: 0 26px 50px -20px rgba(11, 30, 59, 0.7);
  animation: toast-in 0.7s 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
@keyframes toast-in { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
.toast-check { width: 36px; height: 36px; border-radius: 50%; border: 2px solid var(--na-brass); background: rgba(184, 146, 90, 0.2); display: flex; align-items: center; justify-content: center; }
.toast-title { font-weight: 700; font-size: 0.98rem; }
.toast-sub { font-size: 0.82rem; color: rgba(243, 238, 227, 0.7); }

/* ═════════════ Sections ═════════════ */
.section-head { max-width: 700px; margin-bottom: 52px; }

.steps { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.step { padding: 32px 30px; }
.step-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 26px; }
.step-num { font-family: var(--na-serif); font-weight: 700; font-size: 3rem; line-height: 1; color: var(--na-navy); }
.step-text { margin: 0; font-family: var(--na-serif); font-size: 1.06rem; line-height: 1.65; color: var(--na-ink-2); }

.day-grid { display: grid; grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr); gap: 72px; align-items: start; }
.day-intro { position: sticky; top: 120px; }
.day-list { list-style: none; padding: 0; margin: 0; border-left: 1px solid #CDBF9F; }
.day-item { position: relative; display: grid; grid-template-columns: 104px 1fr; gap: 20px; padding: 20px 20px 20px 32px; }
.day-item::before { content: ''; position: absolute; left: -6px; top: 28px; width: 11px; height: 11px; border-radius: 50%; background: var(--na-paper-2); border: 2px solid var(--na-navy); }
.day-breaking { background: var(--na-card); border-radius: 0 10px 10px 0; box-shadow: 0 18px 36px -28px rgba(16, 27, 45, 0.5); }
.day-breaking::before { background: var(--na-press); border-color: var(--na-press); }
.day-breaking .day-time { color: var(--na-press); }
.day-time { font-weight: 700; font-size: 0.95rem; color: var(--na-brass-text); padding-top: 5px; white-space: nowrap; }
.day-title { font-family: var(--na-serif); font-weight: 700; font-size: 1.25rem; margin: 0 0 4px; }
.day-text { margin: 0; font-family: var(--na-serif); font-size: 1.04rem; line-height: 1.6; color: var(--na-ink-2); max-width: 34em; }

.channels-grid { display: grid; grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); gap: 72px; align-items: center; }
.channels-link { margin-top: 28px; }
.channels { list-style: none; padding: 0; margin: 0; }
.channel { display: flex; gap: 18px; padding: 26px 28px; }
.channel + .channel { border-top: 1px solid var(--na-rule); }
.channel-icon { width: 48px; height: 48px; border-radius: 50%; background: #EEE6D6; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.channel-title { font-family: var(--na-serif); font-weight: 700; font-size: 1.2rem; margin: 0 0 4px; }
.channel-text { margin: 0; font-size: 0.98rem; line-height: 1.6; color: var(--na-ink-2); }

.community { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; border-top: 1px solid #CDBF9F; }
.community-item { padding: 30px 28px 34px 0; border-bottom: 1px solid #CDBF9F; }
.community-item:not(:nth-child(3n + 1)) { padding-left: 28px; border-left: 1px solid #CDBF9F; }
.community-icon { width: 42px; height: 42px; border-radius: 50%; background: var(--na-card); border: 1px solid var(--na-rule); display: flex; align-items: center; justify-content: center; }
.community-title { font-family: var(--na-serif); font-weight: 700; font-size: 1.2rem; margin: 14px 0 6px; }
.community-text { margin: 0; font-size: 0.98rem; line-height: 1.6; color: var(--na-ink-2); }

.pricing-head { max-width: 760px; }

.final { text-align: center; position: relative; overflow: hidden; }
.final::before { content: ''; position: absolute; inset: 0; background: radial-gradient(60% 80% at 50% 0%, rgba(184, 146, 90, 0.16), transparent 70%); pointer-events: none; }
.final-inner { position: relative; max-width: 860px; }
.final-orn { max-width: 340px; margin: 0 auto 22px; }
.final-kicker { font-family: var(--na-serif); font-style: italic; font-size: 1.3rem; color: var(--na-brass); margin: 0 0 8px; }
.final-title { font-size: clamp(2.3rem, 4.6vw, 3.6rem); }
.final-lede { margin: 0 auto; }
.final-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; margin-top: 36px; }

/* ═════════════ Responsive ═════════════ */
@media (max-width: 1100px) {
  .hero-art { transform: scale(0.9); transform-origin: top center; }
}
@media (max-width: 960px) {
  .hero-grid, .day-grid, .channels-grid { grid-template-columns: 1fr; gap: 48px; }
  .hero-art { height: 640px; max-width: 520px; margin: 0 auto; width: 100%; transform: none; }
  .day-intro { position: static; }
  .steps { grid-template-columns: 1fr; }
  .community { grid-template-columns: 1fr 1fr; }
  .community-item, .community-item:not(:nth-child(3n + 1)) { padding: 26px 20px 28px 0; border-left: 0; }
  .community-item:nth-child(2n) { padding-left: 20px; border-left: 1px solid #CDBF9F; }
  .hero-margin { display: none; }
}
@media (max-width: 600px) {
  .hero-art { height: 600px; }
  .paper { width: 240px; left: -10px; top: 40px; }
  .phone { width: 270px; height: 560px; right: -4px; }
  .mail-card { display: none; }
  .toast { left: 0; bottom: 20px; right: 20px; }
  .hero-stats { grid-template-columns: repeat(3, 1fr); }
  .hero-stats div { padding-right: 12px; margin-right: 12px; }
  .hero-stats dd { font-size: 1.7rem; }
  .hero-actions .na-btn, .final-actions .na-btn { width: 100%; }
  .community { grid-template-columns: 1fr; }
  .community-item:nth-child(2n) { padding-left: 0; border-left: 0; }
  .day-item { grid-template-columns: 1fr; gap: 4px; }
  .dateline { letter-spacing: 0.1em; font-size: 0.7rem; }
}
@media (prefers-reduced-motion: reduce) { .toast { animation: none; } }
</style>